export const MEETING_EVENT_METADATA_FIELD = "com.dailyfixme.meeting";
export const SHARED_CALL_SUMMARY_FIELD = "com.dailyfixme.call_summary";
export const WEB_CALL_SUMMARY_FIELD = "com.memotivationapp.call_summary";
export const MEETING_RING_LIFETIME_MS = 60_000;

export type MeetingEventMetadata = {
    meetingUuid: string;
    callId: string;
    chatUuid: string;
    chatRoomId: string;
    matrixRoomId: string;
};

export type SharedCallSummary = {
    startedAt: number;
    endedAt?: number;
    expiresAt: number;
    participantIds: string[];
    initiatorId: string;
    hasNotes?: boolean;
};

export type SharedCallSummaryUpdate = Partial<SharedCallSummary>;

type LegacyMeetingEventContent = {
    meeting_uuid?: unknown;
    meeting_room_id?: unknown;
    call_id?: unknown;
};

type MeetingEventContent = LegacyMeetingEventContent & Record<string, unknown>;

const isString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

export const createMeetingEventMetadata = (metadata: MeetingEventMetadata): MeetingEventMetadata => ({
    meetingUuid: metadata.meetingUuid,
    callId: metadata.callId,
    chatUuid: metadata.chatUuid,
    chatRoomId: metadata.chatRoomId,
    matrixRoomId: metadata.matrixRoomId,
});

export const getMeetingEventMetadata = (content: MeetingEventContent): MeetingEventMetadata | null => {
    const metadata = content[MEETING_EVENT_METADATA_FIELD] as Partial<MeetingEventMetadata> | undefined;
    if (
        metadata &&
        isString(metadata.meetingUuid) &&
        isString(metadata.callId) &&
        isString(metadata.chatUuid) &&
        isString(metadata.chatRoomId) &&
        isString(metadata.matrixRoomId)
    ) {
        return metadata as MeetingEventMetadata;
    }

    if (!isString(content.meeting_uuid) || !isString(content.meeting_room_id)) {
        return null;
    }

    return {
        meetingUuid: content.meeting_uuid,
        callId: isString(content.call_id) ? content.call_id : "",
        chatUuid: "",
        chatRoomId: "",
        matrixRoomId: content.meeting_room_id,
    };
};

export const createSharedCallSummary = (summary: SharedCallSummary): SharedCallSummary => ({
    startedAt: summary.startedAt,
    ...(summary.endedAt ? { endedAt: summary.endedAt } : {}),
    expiresAt: summary.expiresAt,
    participantIds: Array.from(new Set(summary.participantIds.filter(Boolean))),
    initiatorId: summary.initiatorId,
    ...(typeof summary.hasNotes === "boolean" ? { hasNotes: summary.hasNotes } : {}),
});

export const getSharedCallSummaryUpdate = (content: MeetingEventContent): SharedCallSummaryUpdate | null => {
    const summary = content[SHARED_CALL_SUMMARY_FIELD] as SharedCallSummaryUpdate | undefined;
    if (!summary || typeof summary !== "object") {
        return null;
    }

    const hasKnownField = ["startedAt", "endedAt", "expiresAt", "participantIds", "initiatorId", "hasNotes"]
        .some(field => Object.prototype.hasOwnProperty.call(summary, field));
    const fieldsAreValid =
        (summary.startedAt === undefined || typeof summary.startedAt === "number") &&
        (summary.endedAt === undefined || typeof summary.endedAt === "number") &&
        (summary.expiresAt === undefined || typeof summary.expiresAt === "number") &&
        (summary.participantIds === undefined || (
            Array.isArray(summary.participantIds) && summary.participantIds.every(isString)
        )) &&
        (summary.initiatorId === undefined || isString(summary.initiatorId)) &&
        (summary.hasNotes === undefined || typeof summary.hasNotes === "boolean");

    return hasKnownField && fieldsAreValid ? summary : null;
};

export const getSharedCallSummary = (content: MeetingEventContent): SharedCallSummary | null => {
    const summary = getSharedCallSummaryUpdate(content);
    if (
        !summary ||
        typeof summary.startedAt !== "number" ||
        typeof summary.expiresAt !== "number" ||
        !Array.isArray(summary.participantIds) ||
        !summary.participantIds.every(isString) ||
        !isString(summary.initiatorId) ||
        (summary.endedAt !== undefined && typeof summary.endedAt !== "number")
    ) {
        return null;
    }

    return summary as SharedCallSummary;
};

export const mergeSharedCallSummaryUpdates = (
    updates: SharedCallSummaryUpdate[]
): SharedCallSummaryUpdate | null => {
    if (updates.length === 0) {
        return null;
    }

    return updates.reduce<SharedCallSummaryUpdate>((merged, update) => ({
        ...merged,
        ...Object.fromEntries(Object.entries(update).filter(([, value]) => value !== undefined)),
        ...(update.participantIds ? {
            participantIds: Array.from(new Set([
                ...(merged.participantIds || []),
                ...update.participantIds
            ]))
        } : {}),
        ...(merged.hasNotes === true || update.hasNotes === true
            ? { hasNotes: true }
            : typeof update.hasNotes === "boolean"
                ? { hasNotes: update.hasNotes }
                : {})
    }), {});
};

export const getSharedCallHasNotes = (updates: SharedCallSummaryUpdate[]): boolean => {
    return mergeSharedCallSummaryUpdates(updates)?.hasNotes ?? true;
};

export const getMeetingRecipientIds = (
    users: Array<{ matrixUserId?: string }> | undefined,
    senderId: string
): string[] => Array.from(new Set(
    (users || [])
        .map(user => user.matrixUserId || "")
        .filter(userId => userId && userId !== senderId)
));

export const getLatestMembershipExpiry = (
    memberships: Array<{ getAbsoluteExpiry?: () => number }> | undefined,
    fallbackTimestamp: number
): number => Math.max(
    fallbackTimestamp,
    ...(memberships || []).map(membership => membership.getAbsoluteExpiry?.() || fallbackTimestamp)
);
